import { useEffect, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { WS_EVENTS } from '@easyedit/shared'
import { api } from '../../lib/api/client'
import type { MediaAsset, Project, SequenceResponse } from '../../lib/api/types'
import { on, subscribeProject, unsubscribeProject } from '../../lib/ws/client'
import { useEditorStore } from '../../stores/editorStore'
import { Topbar } from './Topbar'
import { Sidebar } from './Sidebar'
import { Preview } from './Preview'
import { Inspector } from './Inspector'
import { Timeline } from './timeline/Timeline'
import { Toasts } from './Toasts'

export function EditorScreen({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient()
  const load = useEditorStore((s) => s.load)
  const applyRemote = useEditorStore((s) => s.applyRemote)
  const sequence = useEditorStore((s) => s.sequence)

  const project = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
  })

  const sequenceId = project.data?.sequences?.[0]?.id

  const sequenceQuery = useQuery({
    queryKey: ['sequence', sequenceId],
    queryFn: () => api.get<SequenceResponse>(`/sequences/${sequenceId}`),
    enabled: Boolean(sequenceId),
  })

  const assets = useQuery({
    queryKey: ['media', projectId],
    queryFn: () => api.get<MediaAsset[]>(`/projects/${projectId}/media`),
  })

  useEffect(() => {
    if (sequenceQuery.data) load(sequenceQuery.data.sequence, projectId)
  }, [sequenceQuery.data, load, projectId])

  useEffect(() => {
    subscribeProject(projectId)
    const offs = [
      // Um asset que ficou pronto precisa aparecer na biblioteca sem F5.
      on(WS_EVENTS.MEDIA_READY, () => {
        void queryClient.invalidateQueries({ queryKey: ['media', projectId] })
      }),
      on(WS_EVENTS.TRANSCRIPT_READY, () => {
        void queryClient.invalidateQueries({ queryKey: ['transcript', projectId] })
        void queryClient.invalidateQueries({ queryKey: ['transcriptions', projectId] })
      }),
      on(WS_EVENTS.EXPORT_READY, () => {
        void queryClient.invalidateQueries({ queryKey: ['exports', projectId] })
      }),
      on(WS_EVENTS.SEQUENCE_COMMAND, (event) => {
        // Comandos de outro cliente ou de um job de IA chegam como comandos, não como
        // a timeline inteira: centenas de bytes em vez de megabytes.
        const commands = event.commands as Parameters<typeof applyRemote>[0]
        if (Array.isArray(commands)) applyRemote(commands)
      }),
    ]
    return () => {
      offs.forEach((off) => off())
      unsubscribeProject(projectId)
    }
  }, [projectId, queryClient, applyRemote])

  const assetMap = useMemo(() => {
    const map = new Map<string, MediaAsset>()
    for (const asset of assets.data ?? []) map.set(asset.id, asset)
    return map
  }, [assets.data])

  if (project.isLoading || sequenceQuery.isLoading) {
    return <div className="h-full grid place-items-center text-sm text-ink-400">Abrindo projeto…</div>
  }
  if (project.isError || !project.data) {
    return <div className="h-full grid place-items-center text-sm text-ink-400">Projeto não encontrado.</div>
  }

  return (
    <div className="h-full flex flex-col bg-ink-900">
      <Topbar project={project.data} />
      <div className="flex-1 min-h-0 flex">
        <Sidebar project={project.data} assets={assets.data ?? []} />
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex-1 min-h-0 flex">
            <Preview assets={assetMap} />
            <Inspector assets={assetMap} />
          </div>
          <Timeline assets={assetMap} />
        </div>
      </div>
      <Toasts />
      {!sequence && (
        <div className="absolute inset-0 grid place-items-center bg-ink-900/80 text-sm text-ink-400">
          Carregando timeline…
        </div>
      )}
    </div>
  )
}
